import {Meta} from '@rdcomponents/meta/meta';
import {Typography} from 'antd';
import {useContext, useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {appRoute} from '../../../../../app-route';
import {BreadCrumbs} from '../../../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../../../layout/page/page';
import {MainPageContainer} from '../../../../../layout/page-card/main-page-container';
import {PageCard} from '../../../../../layout/page-card/page-card';
import {PageHeader} from '../../../../../layout/page-header/page-header';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../provider/locale/localization';
import {MainFilterContext} from '../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../provider/main-filter/main-filter-type';
import {useReviewAnalyticsTagBySentiment} from '../../../../../service/reivew-analysis/review-analytics-tags/review-analytics-tag-hook';
import {useAspects} from '../../../../../service/reivew-analysis/review-aspect/review-aspect-hook';
import {useReviewsCountByAspects} from '../../../../../service/reivew-analysis/review-aspects/review-aspects-hook';
import {useCompetitorsByAspect} from '../../../../../service/reivew-analysis/review-competitors-by-aspect/review-competitors-by-aspect-hook';
import {AiSummaryBlock} from '../../components/ai-summary-block/ai-summary-block';
import {AspectScatter, ScatterPointType} from '../../components/aspect-scatter/aspect-scatter';
import {useReviewsAnalysisDefaultDateRange, useReviewsAnalysisFilter} from '../../reviews-analysis-hook';
import {TagCloudChart} from '../dashboard/widgets/tag-cloud/tag-cloud-chart/tag-cloud-chart';
import {tagCloudFetchCount, tagCloudSingleLimit} from '../dashboard/widgets/tag-cloud/tag-cloud-const';

export function AspectPage(): JSX.Element {
    const {aspectId: aspectIdParameter} = useParams<{aspectId: string}>();
    const aspectId = Number(aspectIdParameter);

    const {getLocalizedString} = useLocale();
    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);
    // Opened outside the filter drawer — seed a default period so date-scoped endpoints
    // (tags, AI summary) don't query all-time or 400 on empty dates.
    const defaultDateRange = useReviewsAnalysisDefaultDateRange();
    const {singleFilter} = useReviewsAnalysisFilter(true, defaultDateRange);

    const {reviewsAnalysisDashboard, reviewsAnalysisKeywordsAndTopics, reviewsAnalysisAspect} = appRoute;

    // Resolve the aspect title for the header and to match the own scatter point
    // (reviews-by-aspects rows are keyed by the localized aspect title, not id).
    const {data: aspects} = useAspects('', [aspectId]);
    const aspectTitle = aspects?.find((aspect) => aspect.ids.includes(aspectId))?.title ?? aspects?.[0]?.title ?? '';

    // Stable reference so AiSummaryBlock's generate callback isn't recreated on every render,
    // and so the tags hook doesn't refetch in a loop (it keys pagination on aspectIds).
    const aspectIds = useMemo(() => [aspectId], [aspectId]);

    const tagCloudOptions = useMemo(() => ({...singleFilter, aspectIds}), [singleFilter, aspectIds]);

    const tagCloud = useReviewAnalyticsTagBySentiment(tagCloudOptions, mainFilterKey, {
        initialPageSize: tagCloudFetchCount,
        shouldSaveState: false,
    });

    const competitors = useCompetitorsByAspect(aspectId);
    const ownAspects = useReviewsCountByAspects();

    const scatterPoints: Array<ScatterPointType> = useMemo(() => {
        const points: Array<ScatterPointType> = (competitors.data ?? []).map((brand) => ({
            name: brand.brandName,
            tonalityIdx: brand.tonalityIdx,
            mentions: brand.mentions,
            isOwn: false,
        }));

        const own = ownAspects.data?.reviewsByAspects.find((aspect) =>
            aspect.ids && aspect.ids.length > 0 ? aspect.ids.includes(aspectId) : aspect.aspect === aspectTitle
        );

        if (own) {
            points.push({
                name: '',
                tonalityIdx: own.currentPeriodData.tonalityIdx,
                mentions: own.currentPeriodData.reviewsCount,
                isOwn: true,
            });
        }

        return points;
    }, [competitors.data, ownAspects.data, aspectTitle, aspectId]);

    return (
        <Page renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <Meta title={getLocalizedString('REVIEWS_ANALYSIS__ASPECT__TITLE')} />

            <PageCard>
                <BreadCrumbs
                    list={[
                        {path: reviewsAnalysisDashboard.path, titleLangKey: 'CATEGORY_NAME__REVIEWS_ANALYSIS'},
                        {
                            path: reviewsAnalysisKeywordsAndTopics.path,
                            titleLangKey: 'CATEGORY_NAME__REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS',
                        },
                        {
                            path: reviewsAnalysisAspect.path,
                            titleLangKey: 'REVIEWS_ANALYSIS__ASPECT__TITLE',
                        },
                    ]}
                />

                <PageHeader>
                    <Typography.Title level={4}>
                        {getLocalizedString('REVIEWS_ANALYSIS__ASPECT__TITLE')} «{aspectTitle}»
                    </Typography.Title>
                </PageHeader>
            </PageCard>

            <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECT__KEYWORDS_TITLE" />}>
                <TagCloudChart
                    data={tagCloud.result?.results || []}
                    isInProgress={tagCloud.isInProgress}
                    processError={tagCloud.processError}
                    tagLimit={tagCloudSingleLimit}
                />
            </PageCard>

            <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECT__AI_SUMMARY_TITLE" />}>
                <AiSummaryBlock aspectIds={aspectIds} />
            </PageCard>

            <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECT__COMPETITORS_TITLE" />}>
                <AspectScatter points={scatterPoints} />
            </PageCard>
        </Page>
    );
}
